import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/ResponsiveDataTable';
import { Link } from 'react-router-dom';
import { getAllVBAs, updateDetailAndCreateVBA, checkUserByPhone, getUserPaymentsByPhone } from '../config/api';
import { toast } from "react-toastify";
import useIsMobile from '../hooks/useIsMobile';

const getVbaGstNumber = (vba) => {
  const profileGst = String(vba?.user?.gst_number || '').trim();
  if (profileGst) return profileGst;
  return String(vba?.latest_order_gst_number || '').trim();
};

const VirtualBankAccounts = () => {
  const isMobile = useIsMobile();
  const [vbas, setVbas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');

  // Ledger modal
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [ledgerUser, setLedgerUser] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerBalance, setLedgerBalance] = useState(0);
  const [loadingLedger, setLoadingLedger] = useState(false);
  
  // Create VBA modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createStep, setCreateStep] = useState(1); // 1: phone, 2: details, 3: success
  const [createPhone, setCreatePhone] = useState('');
  const [createName, setCreateName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdVBA, setCreatedVBA] = useState(null);

  // Only superAdmin can create DBA accounts
  const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
  const isSuperAdmin = userData.role === 'superAdmin';

  useEffect(() => {
    loadVBAs();
  }, []);

  const loadVBAs = async () => {
    setLoading(true);
    try {
      const data = await getAllVBAs() || {};
      setVbas(data.data || []);
    } catch (error) {
      console.error('Error loading VBAs:', error);
      toast.error('Failed to load dedicated bank accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleViewLedger = async (vba) => {
    setShowLedgerModal(true);
    setLedgerUser({ name: vba.virtual_account_name, phone: vba.virtual_account_phone, email: vba.virtual_account_email });
    setLedgerEntries([]);
    setLedgerBalance(0);
    setLoadingLedger(true);
    try {
      const response = await getUserPaymentsByPhone(vba.virtual_account_phone);
      if (response.success && response.data) {
        setLedgerEntries(response.data.entries || []);
        setLedgerBalance(response.data.balance || 0);
      }
    } catch (error) {
      toast.error('Failed to load ledger');
    } finally {
      setLoadingLedger(false);
    }
  };

  const handleCreateVBA = () => {
    setShowCreateModal(true);
    setCreateStep(1);
    setCreatePhone('');
    setCreateName('');
    setCreateEmail('');
    setCreatedVBA(null);
  };

  const handlePhoneSubmit = async () => {
    if (!createPhone || createPhone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setCreating(true);
    try {
      const response = await checkUserByPhone(createPhone);
      const result = response.data || response; // Handle both response formats
      
      console.log('Check user response:', result); // Debug log
      
      if (result.vbaExists) {
        // Case A: VBA already exists - go directly to success screen
        toast.info('DBA account already exists for this phone number');
        setCreatedVBA(result.data);
        setCreateStep(3);
        loadVBAs();
      } else if (result.missingFields && result.missingFields.length > 0) {
        // Case C/D: Need to collect missing fields
        // Pre-fill existing data if available
        if (result.data) {
          setCreateName(result.data.name || '');
          setCreateEmail(result.data.email || '');
        }
        setCreateStep(2);
      } else {
        // Case B: All details present, create VBA directly
        const createResult = await updateDetailAndCreateVBA(
          createPhone,
          result.data.name,
          result.data.email
        );
        
        if (createResult.success && createResult.data) {
          setCreatedVBA(createResult.data);
          setCreateStep(3);
          loadVBAs();
          toast.success('DBA account created successfully!');
        }
      }
    } catch (error) {
      console.error('Error checking user:', error);
      toast.error(error.message || 'Failed to process request');
    } finally {
      setCreating(false);
    }
  };

  const handleDetailsSubmit = async () => {
    if (!createName || !createEmail) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setCreating(true);
    try {
      const result = await updateDetailAndCreateVBA(createPhone, createName, createEmail);
      
      if (result.success && result.data) {
        setCreatedVBA(result.data);
        setCreateStep(3);
        loadVBAs(); // Refresh list
        toast.success('DBA account created successfully!');
      } else {
        toast.error(result.message || 'Failed to create DBA account');
      }
    } catch (error) {
      console.error('Error creating DBA:', error);
      toast.error(error.message || 'Failed to create DBA account');
    } finally {
      setCreating(false);
    }
  };

  const copyVBADetails = (vba) => {
    const details = `Account Number: ${vba.vba_account_number}
Account Name: ENERGYBRIDGE TECH INDIA PRIVATE LIMITED
IFSC: ${vba.vba_ifsc}
Bank: ${vba.vba_bank_code}`;
    
    navigator.clipboard.writeText(details);
    toast.success('DBA details copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'ACTIVE': { bg: '#198754', text: 'white' },
      'INACTIVE': { bg: '#6c757d', text: 'white' },
      'PENDING': { bg: '#ffc107', text: '#000' },
    };
    
    const colors = statusColors[status] || { bg: '#6c757d', text: 'white' };
    
    return (
      <span 
        className="status-badge" 
        style={{ 
          backgroundColor: colors.bg, 
          color: colors.text,
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '600'
        }}
      >
        {status}
      </span>
    );
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const columns = [
    {
      name: 'Account ID',
      selector: row => row.virtual_account_id,
      sortable: true,
      width: '140px',
      cell: row => (
        <div style={{ fontWeight: '600', color: '#0d6efd', fontFamily: 'monospace' }}>
          {row.virtual_account_id}
        </div>
      ),
    },
    {
      name: 'User Details',
      selector: row => row.virtual_account_name,
      sortable: true,
      width: '220px',
      cell: row => (
        <div>
          <div style={{ fontWeight: '600', color: '#212529' }}>
            {row.virtual_account_name || '-'}
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>
            {row.virtual_account_phone}
          </div>
        </div>
      ),
    },
    {
      name: 'Bank Details',
      selector: row => row.vba_account_number,
      sortable: true,
      width: '280px',
      cell: row => (
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: '600' }}>
            {row.vba_account_number}
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>
            IFSC: {row.vba_ifsc} | {row.vba_bank_code}
          </div>
        </div>
      ),
    },
    {
      name: 'Email',
      selector: row => row.virtual_account_email,
      sortable: true,
      width: '200px',
      cell: row => (
        <div style={{ fontSize: '13px' }}>
          {row.virtual_account_email || '-'}
        </div>
      ),
    },
    {
      name: 'GST',
      selector: row => getVbaGstNumber(row),
      sortable: true,
      width: '180px',
      cell: row => (
        <div style={{ fontFamily: 'monospace', fontSize: '13px' }}>
          {getVbaGstNumber(row) || '-'}
        </div>
      ),
    },
    {
      name: 'Status',
      selector: row => row.vba_status,
      sortable: true,
      width: '120px',
      cell: row => getStatusBadge(row.vba_status),
    },
    {
      name: 'Created On',
      selector: row => row.vba_created_on,
      sortable: true,
      width: '180px',
      cell: row => (
        <div style={{ fontSize: '13px' }}>
          {formatDateTime(row.vba_created_on)}
        </div>
      ),
    },
    {
      name: 'Current Balance',
      selector: row => parseFloat(row.current_balance) || 0,
      sortable: true,
      width: '160px',
      cell: row => {
        const bal = row.current_balance;
        return (
          <button
            onClick={() => handleViewLedger(row)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontWeight: '700', fontSize: '14px',
              color: bal === null || bal === undefined ? '#6c757d' : parseFloat(bal) >= 0 ? '#198754' : '#dc3545'
            }}
            title="Click to view ledger"
          >
            {bal === null || bal === undefined ? '—' : `₹${parseFloat(bal).toFixed(2)}`}
          </button>
        );
      }
    },
    {
      name: 'Actions',
      cell: row => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="btn btn-secondary"
            style={{ fontSize: '12px', padding: '6px 12px' }}
            onClick={() => copyVBADetails(row)}
            title="Copy DBA Details"
          >
            📋 Copy
          </button>
        </div>
      ),
      ignoreRowClick: true,
      width: '120px',
    },
  ];

  const filteredItems = vbas.filter(
    item => {
      const searchText = filterText.toLowerCase();
      return (
        item.virtual_account_id.toLowerCase().includes(searchText) ||
        (item.virtual_account_name && item.virtual_account_name.toLowerCase().includes(searchText)) ||
        (item.virtual_account_phone && item.virtual_account_phone.includes(searchText)) ||
        (item.vba_account_number && item.vba_account_number.includes(searchText)) ||
        getVbaGstNumber(item).toLowerCase().includes(searchText) ||
        (item.vba_status && item.vba_status.toLowerCase().includes(searchText))
      );
    }
  );

  const subHeaderComponent = useMemo(() => {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        width: '100%',
        padding: '8px 0'
      }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: '300px' }}>
          <input
            type="text"
            placeholder="Search VBAs..."
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            style={{
              padding: '10px 14px',
              border: '2px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px',
              width: '100%',
              transition: 'all 0.3s ease',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#0d6efd';
              e.target.style.boxShadow = '0 0 0 2px rgba(13, 110, 253, 0.25)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#ced4da';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          {isSuperAdmin && (
            <button
              className="btn btn-success"
              onClick={handleCreateVBA}
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              + Create DBA Account
            </button>
          )}
          <div style={{ 
            padding: '8px 16px', 
            backgroundColor: '#e7f3ff', 
            border: '1px solid #b3d9ff',
            borderRadius: '6px',
            fontSize: '14px',
            color: '#0056b3'
          }}>
            <strong>Total VBAs:</strong> {vbas.length}
          </div>
          <div style={{ 
            padding: '8px 16px', 
            backgroundColor: '#d1e7dd', 
            border: '1px solid #badbcc',
            borderRadius: '6px',
            fontSize: '14px',
            color: '#0f5132'
          }}>
            <strong>Active:</strong> {vbas.filter(v => v.vba_status === 'ACTIVE').length}
          </div>
          <div style={{ 
            padding: '8px 16px', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffc107',
            borderRadius: '6px',
            fontSize: '14px',
            color: '#856404'
          }}>
            <strong>Total Balance:</strong> ₹{vbas.reduce((sum, v) => sum + (parseFloat(v.current_balance) || 0), 0).toFixed(2)}
          </div>
        </div>
      </div>
    );
  }, [filterText, vbas]);

  const customStyles = {
    header: {
      style: {
        minHeight: '56px',
        paddingLeft: '0px',
        paddingRight: '0px',
      },
    },
    headRow: {
      style: {
        borderTopStyle: 'solid',
        borderTopWidth: '1px',
        borderTopColor: '#dee2e6',
        backgroundColor: '#f8f9fa',
      },
    },
    headCells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#dee2e6',
        },
        backgroundColor: '#f8f9fa',
        fontWeight: '600',
        fontSize: '14px',
        color: '#495057',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: '16px',
      },
    },
    cells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#dee2e6',
        },
        padding: '16px',
        fontSize: '14px',
      },
    },
    rows: {
      style: {
        '&:hover': {
          backgroundColor: '#f8f9fa',
        },
        borderBottomColor: '#dee2e6',
      },
    },
    pagination: {
      style: {
        borderTop: '1px solid #dee2e6',
        padding: '16px',
      },
    },
  };

  return (
    <div className="container">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="page-title">Dedicated Bank Accounts</h1>
            {/* <p className="page-subtitle">Manage Cashfree virtual bank accounts</p> */}
          </div>
        </div>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={filteredItems}
          progressPending={loading}
          pagination
          paginationResetDefaultPage={false}
          subHeader
          subHeaderComponent={subHeaderComponent}
          selectableRows={false}
          customStyles={customStyles}
          highlightOnHover
          responsive
          striped
          paginationPerPage={15}
          paginationRowsPerPageOptions={[10, 15, 25, 50]}
          noDataComponent={
            <div style={{ 
              padding: '60px 40px', 
              textAlign: 'center',
              color: '#6c757d',
              fontSize: '16px'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '8px' }}>No dedicated bank accounts found</div>
              <div>DBA accounts will appear here when created</div>
            </div>
          }
          defaultSortFieldId="vba_created_on"
          defaultSortAsc={false}
        />
      </div>

      {/* Create VBA Modal */}
      {showCreateModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => !creating && setShowCreateModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '500px',
              padding: '24px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '2px solid #dee2e6',
              paddingBottom: '16px'
            }}>
              <h2 style={{ margin: 0, fontSize: '24px', color: '#212529' }}>
                Create DBA Account
              </h2>
              <button
                onClick={() => !creating && setShowCreateModal(false)}
                disabled={creating}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: creating ? 'not-allowed' : 'pointer',
                  color: '#6c757d',
                  padding: '0',
                  width: isMobile ? '44px' : '32px',
                  height: isMobile ? '44px' : '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            
            {createStep === 1 && (
              <div>
                <p style={{ marginBottom: '16px', color: '#6c757d' }}>
                  Enter the phone number to create a DBA account
                </p>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Phone Number
                  </label>
                  <input
                    type="text"
                    value={createPhone}
                    onChange={(e) => setCreatePhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="Enter 10-digit phone number"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    maxLength={10}
                  />
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handlePhoneSubmit}
                  disabled={creating || createPhone.length !== 10}
                  style={{ width: '100%', padding: '12px' }}
                >
                  {creating ? 'Processing...' : 'Continue'}
                </button>
              </div>
            )}

            {createStep === 2 && (
              <div>
                <p style={{ marginBottom: '16px', color: '#6c757d' }}>
                  Please provide the following details for phone: <strong>{createPhone}</strong>
                </p>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Name
                  </label>
                  <input
                    type="text"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    placeholder="Enter name"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={createEmail}
                    onChange={(e) => setCreateEmail(e.target.value)}
                    placeholder="Enter email"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleDetailsSubmit}
                  disabled={creating || !createName || !createEmail}
                  style={{ width: '100%', padding: '12px' }}
                >
                  {creating ? 'Creating Account...' : 'Create DBA Account'}
                </button>
              </div>
            )}

            {createStep === 3 && createdVBA && (
              <div>
                <div style={{ 
                  textAlign: 'center', 
                  padding: '20px',
                  backgroundColor: '#d1e7dd',
                  borderRadius: '8px',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '10px' }}>✓</div>
                  <h3 style={{ color: '#0f5132', margin: '0 0 8px 0' }}>
                    DBA Account Created Successfully!
                  </h3>
                </div>
                
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '16px', 
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Account Number:</strong>
                    <div style={{ fontFamily: 'monospace', fontSize: '14px', marginTop: '4px' }}>
                      {createdVBA.vba_account_number}
                    </div>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Account Name:</strong>
                    <div style={{ fontSize: '14px', marginTop: '4px' }}>
                      {createdVBA.virtual_account_name}
                    </div>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>IFSC:</strong>
                    <div style={{ fontFamily: 'monospace', fontSize: '14px', marginTop: '4px' }}>
                      {createdVBA.vba_ifsc}
                    </div>
                  </div>
                  <div>
                    <strong>Bank:</strong>
                    <div style={{ fontSize: '14px', marginTop: '4px' }}>
                      {createdVBA.vba_bank_code}
                    </div>
                  </div>
                </div>

                <button
                  className="btn btn-primary"
                  onClick={() => {
                    copyVBADetails(createdVBA);
                  }}
                  style={{ width: '100%', padding: '12px', marginBottom: '12px' }}
                >
                  📋 Copy All Details
                </button>

                <button
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                  style={{ width: '100%', padding: '12px' }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ledger Modal */}
      {showLedgerModal && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowLedgerModal(false)}
        >
          <div
            style={{ backgroundColor: 'white', borderRadius: '8px', width: '90%', maxWidth: '1100px', maxHeight: '85vh', overflow: 'auto', padding: '24px' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '2px solid #dee2e6', paddingBottom: '16px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '22px', color: '#212529' }}>Ledger — {ledgerUser?.name}</h2>
                <p style={{ margin: '4px 0 0 0', color: '#6c757d', fontSize: '13px' }}>{ledgerUser?.phone} · {ledgerUser?.email}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>Available Balance</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: ledgerBalance >= 0 ? '#28a745' : '#dc3545' }}>
                    ₹{parseFloat(ledgerBalance).toFixed(2)}
                  </div>
                </div>
                <button onClick={() => setShowLedgerModal(false)} style={{ background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#6c757d', ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}>×</button>
              </div>
            </div>

            {loadingLedger ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>Loading ledger...</div>
            ) : ledgerEntries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>No ledger entries found</div>
            ) : (
              <DataTable
                columns={[
                  {
                    name: 'Date',
                    selector: row => row.created_at,
                    sortable: true,
                    width: '160px',
                    format: row => new Date(row.created_at).toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                  },
                  {
                    name: 'Type',
                    selector: row => row.entry_type,
                    sortable: true,
                    width: '150px',
                    cell: row => {
                      const colors = { ORDER: '#dc3545', PAYMENT: '#28a745', MANUAL_PAYMENT: '#007bff', WALLET_TOPUP: '#17a2b8', REFUND: '#ffc107', ADJUSTMENT: '#6c757d' };
                      return <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', color: 'white', backgroundColor: colors[row.entry_type] || '#6c757d' }}>{row.entry_type.replace(/_/g, ' ')}</span>;
                    }
                  },
                  {
                    name: 'Amount',
                    selector: row => row.amount,
                    sortable: true,
                    width: '120px',
                    cell: row => <span style={{ fontWeight: 'bold', color: parseFloat(row.amount) >= 0 ? '#28a745' : '#dc3545' }}>₹{parseFloat(row.amount).toFixed(2)}</span>
                  },
                  {
                    name: 'Order ID',
                    selector: row => row.order_id || '—',
                    width: '130px',
                    cell: row => row.order_id ? <Link to={`/orders/${row.order_id}`} target={isMobile ? undefined : '_blank'} style={{ color: '#007bff' }}>{row.order_id}</Link> : '—'
                  },
                  { name: 'Payment Mode', selector: row => row.payment_mode || '—', width: '130px' },
                  { name: 'Reference', selector: row => row.reference_number || '—', width: '150px' },
                  { name: 'Description', selector: row => row.description || '—', grow: 1 },
                ]}
                data={ledgerEntries}
                pagination
                paginationPerPage={15}
                paginationRowsPerPageOptions={[10, 15, 25, 50]}
                customStyles={customStyles}
                highlightOnHover
                striped
                defaultSortFieldId="created_at"
                defaultSortAsc={false}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VirtualBankAccounts;
